/**
 * Which file an asset's thumbnail shows, read the same way by the asset panel
 * and the exposure strips: the asset's first image, document or video, and a
 * capture's materialized page image before the file it was cut from.
 */
import type { ConceptAsset } from "@digital-twins/shared-types";

import type { LightboxCreative } from "@/components/chat/CreativeLightbox";

type AssetMedia = ConceptAsset["media"][number];

export function primaryAssetMedia(concept: ConceptAsset): AssetMedia | undefined {
  return concept.media.find(
    (media) => media.kind === "image" || media.kind === "document" || media.kind === "video",
  );
}

export function assetMediaUrl(media: AssetMedia | undefined): string {
  return media?.publicUrl || media?.url || media?.filePath || "";
}

export function primaryMediaUrl(concept: ConceptAsset): string {
  return assetMediaUrl(primaryAssetMedia(concept));
}

export function materializedPreviewMedia(
  concept: ConceptAsset,
  mediaId?: string,
): AssetMedia | undefined {
  if (!mediaId) return undefined;
  return concept.media.find((media) => media.kind === "image" && media.sliceOf === mediaId);
}

export function isImageMedia(mimeType?: string): boolean {
  return (mimeType ?? "").startsWith("image/");
}

export function isVideoMedia(
  media: { kind?: string; mimeType?: string } | undefined,
): boolean {
  if (media?.kind === "video") return true;
  return (media?.mimeType ?? "").toLowerCase().startsWith("video/");
}

/**
 * The creative a thumbnail and its lightbox show: the video, the
 * materialized page image of a capture or document, or the image. Null when
 * the asset has no file to show (a message, or a document not yet rendered).
 */
export function assetThumbnailCreative(concept: ConceptAsset): LightboxCreative | null {
  const media = primaryAssetMedia(concept);
  const url = assetMediaUrl(media);
  if (!url) return null;
  if (isVideoMedia(media)) return { src: url, mimeType: media?.mimeType };
  const page = materializedPreviewMedia(concept, media?.id);
  if (page && assetMediaUrl(page)) return { src: assetMediaUrl(page), mimeType: page.mimeType };
  return isImageMedia(media?.mimeType) ? { src: url, mimeType: media?.mimeType } : null;
}
